import React, { useState, useEffect, useRef } from 'react'
import { WaypointResponse } from '@/shared/api/routes'
import { Trash2, ArrowUp, ArrowDown, Clock, Navigation, GripVertical } from '@/ui/icons'
import { useConfirm } from '@/shared/hooks'
import { Button, ConfirmationDialog, Input } from '@/ui/components'
import { useDebouncedCallback } from '@/shared/hooks/useDebouncedCallback'
import { getWaypointTypeStyle, getWaypointTypeLabel } from '../utils/waypointTypeStyles'

interface ItineraryWaypointItemProps {
  waypoint: WaypointResponse
  number: number
  onDelete?: (waypointId: string) => Promise<void>
  onUpdateDay?: (waypointId: string, day: number) => Promise<void>
  onUpdateWaypoint?: (waypointId: string, updates: { poi_type?: string | null; poi_custom_label?: string | null; name?: string }) => Promise<void>
  /** @deprecated Use drag-and-drop instead */
  onMoveUp?: (waypointId: string) => void
  /** @deprecated Use drag-and-drop instead */
  onMoveDown?: (waypointId: string) => void
  /** @deprecated Use drag-and-drop instead */
  canMoveUp?: boolean
  /** @deprecated Use drag-and-drop instead */
  canMoveDown?: boolean
  hasNext?: boolean
  distance?: number // in miles
  duration?: number // in minutes
  travelMode?: 'walking' | 'driving'
  currentDay?: number
  totalDays?: number
  isSelected?: boolean
  onClick?: (waypoint: WaypointResponse) => void
  isDraggable?: boolean
  dragAttributes?: any
  dragListeners?: any
}

export function ItineraryWaypointItem({ 
  waypoint, 
  number, 
  onDelete,
  onUpdateDay,
  onUpdateWaypoint,
  onMoveUp,
  onMoveDown,
  canMoveUp = false,
  canMoveDown = false,
  hasNext = false,
  distance,
  duration,
  travelMode = 'walking',
  currentDay,
  totalDays,
  isSelected = false,
  onClick,
  isDraggable = false,
  dragAttributes,
  dragListeners
}: ItineraryWaypointItemProps) {
  const [isDeleting, setIsDeleting] = useState(false)
  const [isEditingName, setIsEditingName] = useState(false)
  const [localName, setLocalName] = useState(waypoint.name || '')
  const [isUpdatingName, setIsUpdatingName] = useState(false)
  const { confirm, confirmState, close, setLoading: setConfirmLoading } = useConfirm()
  const pendingNameUpdateRef = useRef<{ value: string } | null>(null)

  // Sync localName with waypoint changes
  useEffect(() => {
    if (!isEditingName) {
      setLocalName(waypoint.name || '')
    }
  }, [waypoint.name, isEditingName])

  const [isMoving, setIsMoving] = useState(false)

  const handleMoveUp = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (onMoveUp && canMoveUp && !isMoving) {
      setIsMoving(true)
      onMoveUp(waypoint.id)
      // Reset after a short delay to allow for animation
      setTimeout(() => setIsMoving(false), 300)
    }
  }

  const handleMoveDown = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (onMoveDown && canMoveDown && !isMoving) {
      setIsMoving(true)
      onMoveDown(waypoint.id)
      // Reset after a short delay to allow for animation
      setTimeout(() => setIsMoving(false), 300)
    }
  }

  const location = waypoint.poi_address || 
    (waypoint.latitude && waypoint.longitude
      ? `${waypoint.latitude.toFixed(4)}, ${waypoint.longitude.toFixed(4)}`
      : 'Location not set')

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation()
    
    if (!onDelete) return
    
    await confirm(
      {
        title: 'Delete Waypoint',
        description: `Are you sure you want to delete "${waypoint.name}"? This action cannot be undone.`,
        variant: 'destructive',
        confirmText: 'Delete',
      },
      async () => {
        setIsDeleting(true)
        setConfirmLoading(true)
        try {
          await onDelete(waypoint.id)
        } catch (error) {
          throw error
        } finally {
          setIsDeleting(false)
          setConfirmLoading(false)
        }
      }
    )
  }

  const formatDistance = (miles?: number): string => {
    if (!miles || miles === 0) return ''
    if (miles < 0.1) return '<0.1 mi'
    if (miles < 1) return `${miles.toFixed(1)} mi`
    return `${Math.round(miles)} mi`
  }

  const formatDuration = (minutes?: number): string => {
    if (!minutes || minutes === 0) return ''
    if (minutes < 1) return '<1 min'
    return `${Math.round(minutes)} min`
  }

  // Store waypoint ID and onUpdateWaypoint in refs to avoid stale closures in debounced callback
  const waypointIdRef = useRef(waypoint.id)
  const onUpdateWaypointRef = useRef(onUpdateWaypoint)
  
  useEffect(() => {
    waypointIdRef.current = waypoint.id
    onUpdateWaypointRef.current = onUpdateWaypoint
  }, [waypoint.id, onUpdateWaypoint])

  // Debounced update function for name - only updates after user stops typing for 1500ms
  const debouncedUpdateName = useDebouncedCallback(async (value: string) => {
    const currentOnUpdate = onUpdateWaypointRef.current
    const currentWaypointId = waypointIdRef.current
    
    if (!currentOnUpdate) {
      return
    }

    const trimmedValue = value.trim()
    
    // Don't update if value hasn't changed
    if (trimmedValue === waypoint.name) {
      pendingNameUpdateRef.current = null
      return
    }

    try {
      setIsUpdatingName(true)
      await currentOnUpdate(currentWaypointId, { name: trimmedValue })
      pendingNameUpdateRef.current = null
    } catch (error) {

      // Revert to server value on error
      setLocalName(waypoint.name || '')
      pendingNameUpdateRef.current = null
    } finally {
      setIsUpdatingName(false)
    }
  }, 1500)

  // Immediate update function for blur events
  const updateNameImmediately = async (value: string) => {
    if (!onUpdateWaypoint) {
      return
    }

    // Cancel any pending debounced update
    pendingNameUpdateRef.current = null

    const trimmedValue = value.trim()
    
    // Don't update if value hasn't changed
    if (trimmedValue === waypoint.name) {
      setIsEditingName(false)
      return
    }

    try {
      setIsUpdatingName(true)
      await onUpdateWaypoint(waypoint.id, { name: trimmedValue })
      setIsEditingName(false)

    } catch (error) {

      // Revert to server value on error
      setLocalName(waypoint.name || '')
    } finally {
      setIsUpdatingName(false)
    }
  }

  const handleNameChange = (value: string) => {
    // Update local state immediately for instant UI feedback
    setLocalName(value)
    
    // Store pending update
    pendingNameUpdateRef.current = { value }

    // Debounce the API call
    debouncedUpdateName(value)
  }

  const handleNameBlur = () => {
    // If there's a pending update, execute it immediately
    if (pendingNameUpdateRef.current) {
      updateNameImmediately(pendingNameUpdateRef.current.value)
    } else {
      setIsEditingName(false)
    }
  }

  const handleNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (pendingNameUpdateRef.current) {
        updateNameImmediately(pendingNameUpdateRef.current.value)
      } else {
        setIsEditingName(false)
      }
    } else if (e.key === 'Escape') {
      e.preventDefault()
      setLocalName(waypoint.name || '')
      pendingNameUpdateRef.current = null
      setIsEditingName(false)
    }
  }

  const handleWaypointClick = (e: React.MouseEvent) => {
    // Don't trigger selection if clicking on interactive elements
    const target = e.target as HTMLElement
    if (
      target.closest('button') ||
      target.closest('select') ||
      target.closest('[role="button"]')
    ) {
      return
    }
    onClick?.(waypoint)
  }

  // Get waypoint type style
  const typeStyle = getWaypointTypeStyle(waypoint.poi_type ?? null)
  const TypeIcon = typeStyle.icon
  const typeLabel = getWaypointTypeLabel(waypoint.poi_type ?? null, waypoint.poi_custom_label)

  // Format time display
  const formatTime = (): string => {
    if (waypoint.start_time && waypoint.end_time) {
      return `${waypoint.start_time} - ${waypoint.end_time}`
    } else if (waypoint.start_time) {
      return waypoint.start_time
    }
    return ''
  }

  return (
    <div className="mb-3">
      {/* Color-coded waypoint card */}
      <div 
        className={`relative flex items-start gap-3 rounded-lg border transition-colors ${
          onClick ? 'cursor-pointer' : ''
        } ${
          isSelected 
            ? `bg-white ${typeStyle.borderColor} border-2` 
            : `bg-white ${typeStyle.borderColor} border`
        }`}
        onClick={handleWaypointClick}
      >
        {/* Drag handle (only shown when draggable) */}
        {isDraggable && dragAttributes && dragListeners && (
          <div
            {...dragAttributes}
            {...dragListeners}
            className="flex-shrink-0 flex items-start justify-center w-8 pt-3 cursor-grab active:cursor-grabbing text-earth-400 hover:text-primary transition-colors"
            onClick={(e) => e.stopPropagation()}
            aria-label="Drag to reorder"
          >
            <GripVertical className="w-4 h-4" />
          </div>
        )}
        
        {/* Left edge colored indicator */}
        <div className={`w-1 ${typeStyle.indicatorColor} rounded-l-lg flex-shrink-0`} />
        
        {/* Card content */}
        <div className="flex-1 min-w-0 p-3">
          <div className="flex items-start gap-3">
            {/* Waypoint type icon */}
            <div className={`flex-shrink-0 w-10 h-10 rounded-lg ${typeStyle.bgColor} ${typeStyle.borderColor} border flex items-center justify-center`}>
              <TypeIcon className={`w-5 h-5 ${typeStyle.textColor}`} />
            </div>
            
            {/* Main content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  {isEditingName ? (
                    <div className="mb-1">
                      <Input
                        value={localName}
                        onChange={(e) => handleNameChange(e.target.value)}
                        onBlur={handleNameBlur}
                        onKeyDown={handleNameKeyDown}
                        placeholder="Enter waypoint name"
                        maxLength={255}
                        disabled={isUpdatingName}
                        className="text-body-sm font-medium"
                        autoFocus
                      />
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 mb-1 min-w-0">
                      <h4 
                        className="text-heading-sm text-earth-900 cursor-pointer hover:text-primary transition-colors truncate flex-1 min-w-0"
                        onClick={(e) => {
                          e.stopPropagation()
                          setIsEditingName(true)
                        }}
                        title={waypoint.name || "Click to edit name"}
                      >
                        {waypoint.name}
                      </h4>
                      <span className={`text-caption ${typeStyle.textColor} flex-shrink-0 whitespace-nowrap`}>
                        {typeLabel}
                      </span>
                    </div>
                  )}
                  
                  {/* Time display */}
                  {formatTime() && (
                    <div className="flex items-center gap-1.5 mb-1 text-body-sm text-earth-600">
                      <Clock className="w-3.5 h-3.5" />
                      <span>{formatTime()}</span>
                    </div>
                  )}
                  
                  {/* Location */}
                  <p className="text-body-sm text-earth-600 mb-2">{location}</p>
                  
                  {/* Travel info (distance/duration) */}
                  {hasNext && (distance !== undefined || duration !== undefined) && (
                    <div className="flex items-center gap-1.5 text-caption text-earth-500">
                      <Navigation className="w-3.5 h-3.5" />
                      {distance !== undefined && formatDistance(distance) && (
                        <span>{formatDistance(distance)}</span>
                      )}
                      {duration !== undefined && formatDuration(duration) && (
                        <span>({formatDuration(duration)})</span>
                      )}
                    </div>
                  )}
                </div>

                {/* Action buttons */}
                <div className="flex items-center gap-1 flex-shrink-0">
                  {/* Move up/down buttons (deprecated - only show if not using drag-and-drop) */}
                  {!isDraggable && (onMoveUp || onMoveDown) && (
                    <div className="flex flex-col">
                      <button
                        onClick={handleMoveUp}
                        disabled={!canMoveUp || isMoving}
                        className="flex-shrink-0 text-earth-400 hover:text-primary transition-colors disabled:opacity-30 disabled:cursor-not-allowed p-0.5"
                        aria-label="Move up"
                        title="Move up"
                      >
                        <ArrowUp className="w-4 h-4" />
                      </button>
                      <button
                        onClick={handleMoveDown}
                        disabled={!canMoveDown || isMoving}
                        className="flex-shrink-0 text-earth-400 hover:text-primary transition-colors disabled:opacity-30 disabled:cursor-not-allowed p-0.5"
                        aria-label="Move down"
                        title="Move down"
                      >
                        <ArrowDown className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                  {onDelete && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleDelete}
                      disabled={isDeleting}
                      className="h-6 w-6 p-0 flex-shrink-0 text-destructive hover:text-destructive hover:bg-destructive/10 disabled:opacity-50"
                      aria-label="Delete waypoint"
                      title="Delete waypoint"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ConfirmationDialog
        open={confirmState?.open ?? false}
        onOpenChange={(open) => !open && close()}
        title={confirmState?.title}
        description={confirmState?.description ?? ''}
        confirmText={confirmState?.confirmText}
        cancelText={confirmState?.cancelText}
        variant={confirmState?.variant}
        onConfirm={confirmState?.onConfirm ?? (() => {})}
        isLoading={confirmState?.isLoading}
      />
    </div>
  )
}

